import {
  Alert,
  CheckboxTile,
  Flex,
  Heading,
  HelperText,
} from '@utilitywarehouse/hearth-react';
import {
  IConfigCatClient,
  SettingKeyValue,
  useConfigCatClient,
} from 'configcat-react';
import { useEffect, useState } from 'react';

import { FeatureFlags } from '@constants/featureFlags';
import { useFeatureFlagOverrideContext } from '@providers/FeatureFlagOverrideProvider/FeatureFlagOverrideContext.tsx';
import { flagOverridesMap } from '@utils/flagOverrides.ts';

type HandleOnChange = (flagKey: string, checked: boolean) => void;

type RemoteAndLocalFlagsProps = {
  remoteAndLocalFlags: SettingKeyValue[];
  handleOnChange: HandleOnChange;
};

type RemoteOnlyFlagsProps = {
  remoteOnlyFlags: SettingKeyValue[];
  handleOnChange: HandleOnChange;
};

type LocalOnlyFlagsProps = {
  localOnlyFlags: FeatureFlags[];
  handleOnChange: HandleOnChange;
};

const useGetRemoteFeatureFlags = (client: IConfigCatClient) => {
  const [flags, setFlags] = useState<SettingKeyValue[]>([]);

  useEffect(() => {
    client.getAllValuesAsync().then(setFlags);
  }, [client]);

  return flags;
};

const ConfigCatFeatureFlags = () => {
  const client = useConfigCatClient();
  const remoteFlags = useGetRemoteFeatureFlags(client);
  const { setOverride } = useFeatureFlagOverrideContext();

  const handleOnChange = (flagKey: string, checked: boolean) => {
    setOverride(flagKey, checked);
  };

  const localFlagKeys = new Set<string>(Object.values(FeatureFlags));
  const remoteFlagKeys = new Set(remoteFlags.map((flag) => flag.settingKey));

  const remoteAndLocalFlags = remoteFlags.filter((flag) =>
    localFlagKeys.has(flag.settingKey)
  );
  const remoteOnlyFlags = remoteFlags.filter(
    (flag) => !localFlagKeys.has(flag.settingKey)
  );
  const localOnlyFlags = Object.values(FeatureFlags).filter(
    (flag) => !remoteFlagKeys.has(flag)
  );

  return (
    <>
      <Heading as="h1">ConfigCat feature flags</Heading>

      <Alert
        title="All overrides to feature flags will be reset on page refresh."
        colorScheme="info"
      />

      <RemoteAndLocalFlags
        handleOnChange={handleOnChange}
        remoteAndLocalFlags={remoteAndLocalFlags}
      />

      <Flex direction="column" marginY="200" gap="200">
        <RemoteOnlyFlags
          handleOnChange={handleOnChange}
          remoteOnlyFlags={remoteOnlyFlags}
        />
      </Flex>

      <LocalOnlyFlags
        handleOnChange={handleOnChange}
        localOnlyFlags={localOnlyFlags}
      />
    </>
  );
};

const RemoteAndLocalFlags = ({
  remoteAndLocalFlags,
  handleOnChange,
}: RemoteAndLocalFlagsProps) => (
  <>
    <Heading as="h2" size="sm">
      Local & remote
    </Heading>

    {remoteAndLocalFlags.length === 0 && (
      <HelperText>No feature flags defined.</HelperText>
    )}

    {remoteAndLocalFlags.map((flag) => {
      const flagOverrideKey = Object.keys(flagOverridesMap).find(
        (key) => key === flag.settingKey
      );

      return (
        <CheckboxTile
          onCheckedChange={(checked) =>
            handleOnChange(flag.settingKey, checked)
          }
          defaultChecked={Boolean(
            flagOverrideKey
              ? flagOverridesMap[flagOverrideKey]
              : flag.settingValue
          )}
          label={flag.settingKey}
          key={flag.settingKey}
        />
      );
    })}
  </>
);

const RemoteOnlyFlags = ({
  remoteOnlyFlags,
  handleOnChange,
}: RemoteOnlyFlagsProps) => (
  <>
    <Heading as="h2" size="sm">
      Remote only
    </Heading>

    <Flex direction="column" gap="200">
      <Alert
        title="Remote only feature flags must be changed in ConfigCat."
        colorScheme="warning"
      />

      {remoteOnlyFlags.length === 0 && (
        <HelperText>No remote only feature flags defined.</HelperText>
      )}

      {remoteOnlyFlags.map((remoteFlag) => {
        const flagOverrideKey = Object.keys(flagOverridesMap).find(
          (key) => key === remoteFlag.settingKey
        );

        return (
          <CheckboxTile
            onCheckedChange={(checked) =>
              handleOnChange(remoteFlag.settingKey, checked)
            }
            defaultChecked={Boolean(
              flagOverrideKey
                ? flagOverridesMap[flagOverrideKey]
                : remoteFlag.settingValue
            )}
            label={remoteFlag.settingKey}
            key={remoteFlag.settingKey}
            disabled
          />
        );
      })}
    </Flex>
  </>
);

const LocalOnlyFlags = ({
  localOnlyFlags,
  handleOnChange,
}: LocalOnlyFlagsProps) => (
  <>
    <Heading as="h2" size="sm">
      Local only
    </Heading>

    {localOnlyFlags.length === 0 && (
      <HelperText>No local only feature flags defined.</HelperText>
    )}

    {localOnlyFlags.map((flag) => {
      const flagOverrideKey = Object.keys(flagOverridesMap).find(
        (key) => key === flag
      );

      return (
        <CheckboxTile
          onCheckedChange={(checked) => handleOnChange(flag, checked)}
          defaultChecked={Boolean(
            flagOverrideKey ? flagOverridesMap[flagOverrideKey] : false
          )}
          label={flag}
          key={flag}
        />
      );
    })}
  </>
);

export { ConfigCatFeatureFlags };
